import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, isNil } from 'lodash';

import { FormRow,FormColumn, Input, Select } from 'components/Form';
import { Button } from 'components/Button';
import GeneralError from 'components/GeneralError';
import Validator from 'components/Validator';
import { last4SsnMask, phoneMask, zipMask, numberUnmask } from 'utils/masks';
import { states } from 'utils/states';
import { nextAction } from 'actions/workflow';
import useWorkflowGuard from 'hooks/useWorkflowGuard';

import KBAIdentityVerificationSchema from './schema';
import { useSelector } from 'react-redux';
import { conformToMask } from 'react-text-mask';

const KBAIdentityVerification = ({
  navigate,
  searchParams,
  dispatch,
  validator: { validate, values, setValues, onChangeHandler, errors }
}) => {
  useWorkflowGuard(searchParams, navigate);
  const checkinData = useSelector((state) => get(state, 'workflow.data'));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState(null);

  useEffect(() => {
    if (!isNil(checkinData) && !isEmpty(checkinData)) {
      setValues({
        applicantName: get(checkinData, 'applicantFullName'),
        mobileNumber: get(checkinData, 'phoneNumber') ? conformToMask(get(checkinData, 'phoneNumber'), phoneMask).conformedValue : undefined,
        address: get(checkinData, 'streetAddress1'),
        state: get(checkinData, 'state') ? states.find((s) => s.value === get(checkinData, 'state')) : undefined,
        city: get(checkinData, 'city'),
        zip: get(checkinData, 'zipCode')
      });
    }
  }, [checkinData])

  const handleChange = (field, value) => {
    onChangeHandler(field, value);
  };

  const handleFail = (err) => {
    setGeneralError(get(err, 'data.failure.errors.0'));
    setIsSubmitting(false);
  }

  const handleSubmit = () => {
    if (validate().isValid) {
      setIsSubmitting(true);
      dispatch(nextAction({
        key: searchParams.get('key'),
        data: {
          phoneNumber: numberUnmask(get(values, 'mobileNumber')),
          ssn4: numberUnmask(get(values, 'last4ssn')),
          streetAddress1: get(values, 'address').trim(),
          city: get(values, 'city').trim(),
          state: get(values, 'state.value'),
          zipCode: get(values, 'zip'),
        },
        success: (res) => {
          navigate(get(res, 'data.url'));
          setIsSubmitting(false);
        },
        fail: handleFail,
      }))
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="font-semibold text-slate-600 text-center mb-2">
        ID Check - Personal Information
      </h2>
      <p className="text-sm text-slate-500 text-center px-12">
        Please confirm your home address and phone number. This information, along with your name, will be used to generate a list of questions to verify your identity.
      </p>

      <div className="w-full mb-8">
        <h3 className="text-slate-600 pl-1 font-semibold text-sm mb-2 mt-12">Required Information</h3>

        <FormRow>
          <FormColumn>
            <Input
              label="Applicant Name"
              placeholder="Applicant Name"
              value={values.applicantName}
              error={errors.applicantName}
              onChange={(e) => handleChange('applicantName', e.target.value)}
              required
            />
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <Input
              inputClassName="font-numeric"
              label="Mobile Number"
              placeholder="(+1)"
              mask={phoneMask}
              value={values.mobileNumber}
              error={errors.mobileNumber}
              onChange={(e) => handleChange('mobileNumber', e.target.value)}
              required
            />
          </FormColumn>
          <FormColumn>
            <Input
              inputClassName="font-numeric"
              value={values.last4ssn}
              error={errors.last4ssn}
              onChange={(e) => handleChange('last4ssn', e.target.value)}
              label="Last 4 digits of SSN"
              mask={last4SsnMask}
              placeholder="____"
              required
            />
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <Input
              label="Home Address"
              placeholder="Street Address"
              value={values.address}
              error={errors.address}
              onChange={(e) => handleChange('address', e.target.value)}
              required
            />
          </FormColumn>
        </FormRow>

        <FormRow>
          <FormColumn>
            <Input
              label="City"
              placeholder="City"
              value={values.city}
              error={errors.city}
              onChange={(e) => handleChange('city', e.target.value)}
              required
            />
          </FormColumn>
          <FormColumn>
            <Select
              label="State"
              placeholder="State"
              value={values.state}
              onChange={(val) => handleChange('state', val)}
              options={states}
              error={errors.state}
              required
            />
          </FormColumn>
          <FormColumn className="flex-none">
            <Input
              label="Zip Code"
              placeholder="_____"
              inputClassName="font-numeric"
              value={values.zip}
              onChange={(e) => handleChange('zip', e.target.value)}
              error={errors.zip}
              mask={zipMask}
              required
            />
          </FormColumn>
        </FormRow>
      </div>

      <GeneralError error={generalError} />

      <FormRow className="w-full flex-col-reverse">
        <Button
          className="flex-1"
          label="Submit"
          onClick={handleSubmit}
          isLoading={isSubmitting}
        />
      </FormRow>
    </div>
  );
};

KBAIdentityVerification.propTypes = {
  navigate: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
  validator: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default Validator(KBAIdentityVerificationSchema)(KBAIdentityVerification);
